import {
  BarChartOutlined,
  BellOutlined,
  CheckCircleOutlined,
  MailOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import DOMPurify from 'dompurify'
import type { ComponentType } from 'react'
import styled from 'styled-components'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'

import type { NotificationDto } from 'features/notifications/service/types'

// Backend icon tokens → Antd icon component. Tokens are snake_case
// strings the backend pre-renders into the notification's context (see
// common_lib.notifications producers + bulk_manage background task).
// Unknown tokens fall back to the bell — safer than failing closed and
// invisible, easier to spot in QA than a hardcoded checkmark.
const ICON_MAP: Record<string, ComponentType> = {
  alert_invitation: BellOutlined,
  mail_star: MailOutlined,
  user_add: UserAddOutlined,
  warning: WarningOutlined,
  check_circle: CheckCircleOutlined,
}

function resolveIcon(token: string, kind: string): ComponentType {
  // Shared dashboards read better with a chart glyph than the generic
  // envelope their producer's `mail_star` token maps to (feedback) —
  // dispatch on the row's kind before falling back to the token map.
  if (kind === 'CUSTOM_DASHBOARD') return BarChartOutlined
  return ICON_MAP[token] ?? BellOutlined
}

/**
 * Format an ISO timestamp into an explicit locale-formatted date+time.
 * Pure — no effects, no relative-clock dependency. The bell row used to
 * show "just now / 5m ago"; the operator asked for an explicit timestamp
 * since notifications can sit unread for days and a relative anchor
 * loses meaning quickly.
 */
function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

type Props = {
  readonly notification: NotificationDto
  readonly selected: boolean
  readonly onToggleSelect: (id: number) => void
  readonly onMarkAsRead: (id: number) => void
  readonly onOpenDetail: (notification: NotificationDto) => void
}

/**
 * One row of the notifications panel: selection checkbox, kind icon,
 * title/body/timestamp. Clicking the row auto-reads it and either opens
 * the detail modal (BULK_JOB rows with a structured `detail`) or the
 * row's link in a new tab.
 */
export const NotificationRow = ({
  notification,
  selected,
  onToggleSelect,
  onMarkAsRead,
  onOpenDetail,
}: Props): JSX.Element => {
  const isUnread = notification.acknowledged_at === null
  const sanitizedBody = DOMPurify.sanitize(notification.body_html)
  // BULK_JOB rows whose backend carried a structured `detail` expand
  // inline in a modal — the operator wants to see per-category email
  // lists without leaving the bell. Everything else keeps the legacy
  // "navigate to link" behavior so alerts / dashboards open their
  // canonical pages in a new tab.
  const isExpandable =
    notification.kind === 'BULK_JOB' && notification.detail !== null
  const hasLink = Boolean(notification.link)
  const onRowClick = (): void => {
    if (isUnread) {
      onMarkAsRead(notification.id)
    }
    if (isExpandable) {
      onOpenDetail(notification)
      return
    }
    if (hasLink) {
      window.open(notification.link, '_blank', 'noopener,noreferrer')
    }
  }
  const RowIconComponent = resolveIcon(notification.icon, notification.kind)
  return (
    <Row
      $unread={isUnread}
      $clickable={isExpandable || hasLink}
      data-testid={`notifications-row-${notification.id}`}
      onClick={onRowClick}
    >
      <RowCheckbox
        checked={selected}
        onChange={() => onToggleSelect(notification.id)}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select notification ${notification.id}`}
        data-testid={`notifications-select-${notification.id}`}
      />
      <RowIcon>
        <RowIconComponent />
      </RowIcon>
      <RowBody>
        <RowTitle>{notification.title}</RowTitle>
        {sanitizedBody && (
          <RowHtml
            // body_html is sanitized through DOMPurify above.
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />
        )}
        <RowMeta>
          <RowTimestamp>
            {formatTimestamp(notification.created_at)}
          </RowTimestamp>
        </RowMeta>
      </RowBody>
    </Row>
  )
}

const Row = styled.div<{ $unread: boolean; $clickable: boolean }>`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: ${(p) => (p.$unread ? '#eaf6fe' : '#ffffff')};
  border-bottom: 1px solid #f0f0f0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};

  &:hover {
    background: ${(p) => (p.$unread ? '#dceefb' : '#f7f7f7')};
  }

  &:last-child {
    border-bottom: none;
  }
`

const RowIcon = styled.div`
  flex: 0 0 auto;
  color: #5072a4;
  font-size: 18px;
  padding-top: 2px;
`

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RowTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #000000;
`

const RowHtml = styled.div`
  font-size: 12px;
  color: #595959;

  p {
    margin: 0;
  }
`

const RowMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const RowTimestamp = styled.span`
  font-size: 11px;
  color: #8c8c8c;
`

const RowCheckbox = styled(Checkbox)`
  flex: 0 0 auto;
  padding-top: 2px;
`
